import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {Schema} from 'schemas/SchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {capitalize, constantCaseToWords} from 'util/NameUtil';

import {
  AddColumnTransform,
  ArithmeticTransform,
  ExtraFootersTransform,
  ExtraHeadersTransform,
  DuplicateTransform,
  FilterTransform,
  FindReplaceTransform,
  LabAutomationTransform,
  LabAutomationTransformType,
  MeltTransform,
  MergeTransform,
  PivotTransform,
  PlateShapeTransform,
  SplitTransform,
  CustomTransform,
} from '../../RunSchemaSpecConstants';
import AddColumnForm from './AddColumnForm';
import ArithmeticForm from './ArithmeticForm';
import CustomTransformForm from './CustomTransformForm';
import DuplicateForm from './DuplicateForm';
import ExtraFootersForm from './ExtraFootersForm';
import ExtraHeadersForm from './ExtraHeadersForm';
import FilterForm from './FilterForm';
import FindReplaceForm from './FindAndReplaceForm';
import MeltForm from './MeltForm';
import MergeForm from './MergeForm';
import PivotForm from './PivotForm';
import PlateShapeForm from './PlateShapeForm';
import SplitForm from './SplitForm';
import {
  AddColumnFormErrors,
  ArithmeticFormErrors,
  CustomTransformFormErrors,
  DuplicateFormErrors,
  EmptyFormErrors,
  ExtraFootersFormErrors,
  ExtraHeadersFormErrors,
  FilterFormErrors,
  FindReplaceFormErrors,
  FormErrors,
  getInitialTransformOfType,
  MeltFormErrors,
  MergeFormErrors,
  PivotFormErrors,
  PlateShapeFormErrors,
  SplitFormErrors,
} from './TransformFormUtils';

interface DummyFormProps {
  baseSchema: Schema;
  transform: LabAutomationTransform;
  onChange: (transform: LabAutomationTransform) => void;
  formErrors: FormErrors;
}

export const labAutomationTransformForm = (): {
  [type in LabAutomationTransformType]: (props: DummyFormProps) => JSX.Element | null;
} => ({
  [LabAutomationTransformType.ADD_COLUMN]: (
    props: DummyFormProps & {transform: AddColumnTransform; formErrors: AddColumnFormErrors}
  ) => (
    <AddColumnForm
      baseSchema={props.baseSchema}
      transform={props.transform}
      onChange={props.onChange}
      formErrors={props.formErrors}
    />
  ),
  [LabAutomationTransformType.ARITHMETIC]: (
    props: DummyFormProps & {transform: ArithmeticTransform; formErrors: ArithmeticFormErrors}
  ) => <ArithmeticForm transform={props.transform} onChange={props.onChange} formErrors={props.formErrors} />,
  [LabAutomationTransformType.CUSTOM]: (
    props: DummyFormProps & {transform: CustomTransform; formErrors: CustomTransformFormErrors}
  ) => (
    <CustomTransformForm
      transform={props.transform}
      onChange={props.onChange}
      formErrors={props.formErrors}
    />
  ),
  [LabAutomationTransformType.DUPLICATE]: (
    props: DummyFormProps & {transform: DuplicateTransform; formErrors: DuplicateFormErrors}
  ) => <DuplicateForm transform={props.transform} onChange={props.onChange} formErrors={props.formErrors} />,
  [LabAutomationTransformType.EXTRA_HEADERS]: (
    props: DummyFormProps & {transform: ExtraHeadersTransform; formErrors: ExtraHeadersFormErrors}
  ) => (
    <ExtraHeadersForm transform={props.transform} onChange={props.onChange} formErrors={props.formErrors} />
  ),

  [LabAutomationTransformType.EXTRA_FOOTERS]: (
    props: DummyFormProps & {transform: ExtraFootersTransform; formErrors: ExtraFootersFormErrors}
  ) => (
    <ExtraFootersForm transform={props.transform} onChange={props.onChange} formErrors={props.formErrors} />
  ),
  [LabAutomationTransformType.FILTER]: (
    props: DummyFormProps & {transform: FilterTransform; formErrors: FilterFormErrors}
  ) => (
    <FilterForm
      baseSchema={props.baseSchema}
      transform={props.transform}
      onChange={props.onChange}
      formErrors={props.formErrors}
    />
  ),
  [LabAutomationTransformType.FIND_REPLACE]: (
    props: DummyFormProps & {transform: FindReplaceTransform; formErrors: FindReplaceFormErrors}
  ) => (
    <FindReplaceForm
      baseSchema={props.baseSchema}
      transform={props.transform}
      onChange={props.onChange}
      formErrors={props.formErrors}
    />
  ),
  [LabAutomationTransformType.MERGE]: (
    props: DummyFormProps & {transform: MergeTransform; formErrors: MergeFormErrors}
  ) => <MergeForm transform={props.transform} onChange={props.onChange} formErrors={props.formErrors} />,
  [LabAutomationTransformType.MELT]: (
    props: DummyFormProps & {transform: MeltTransform; formErrors: MeltFormErrors}
  ) => <MeltForm transform={props.transform} onChange={props.onChange} formErrors={props.formErrors} />,
  [LabAutomationTransformType.PLATE_SHAPE]: (
    props: DummyFormProps & {transform: PlateShapeTransform; formErrors: PlateShapeFormErrors}
  ) => <PlateShapeForm transform={props.transform} onChange={props.onChange} formErrors={props.formErrors} />,
  [LabAutomationTransformType.PIVOT]: (
    props: DummyFormProps & {transform: PivotTransform; formErrors: PivotFormErrors}
  ) => <PivotForm transform={props.transform} onChange={props.onChange} formErrors={props.formErrors} />,
  [LabAutomationTransformType.REMOVE_QUOTES]: (props: DummyFormProps) => null,
  [LabAutomationTransformType.SPLIT]: (
    props: DummyFormProps & {transform: SplitTransform; formErrors: SplitFormErrors}
  ) => <SplitForm transform={props.transform} onChange={props.onChange} formErrors={props.formErrors} />,
});

const TRANSFORM_DESCRIPTIONS: {
  [type in LabAutomationTransformType]: string;
} = {
  [LabAutomationTransformType.ADD_COLUMN]: 'Adds a column with a constant value.',
  [LabAutomationTransformType.ARITHMETIC]:
    'Adds column with result of arithmetic between two columns, or one column and a constant.',
  [LabAutomationTransformType.CUSTOM]: '',
  [LabAutomationTransformType.DUPLICATE]: 'Duplicates specified column and appends to end of file.',
  [LabAutomationTransformType.EXTRA_FOOTERS]: 'Removes rows from the bottom of the file.',
  [LabAutomationTransformType.EXTRA_HEADERS]:
    'Removes extra rows above header. Can also concatenate multiple header rows into one row.',
  [LabAutomationTransformType.FILTER]: 'Will keep all rows that meet filter criteria.',
  [LabAutomationTransformType.FIND_REPLACE]: 'Find and replace values.',
  [LabAutomationTransformType.MERGE]:
    'Merges two or more columns together, with data separated by a delimiter.',
  [LabAutomationTransformType.MELT]: 'Unpivots a dataframe from wide to long format',
  [LabAutomationTransformType.PIVOT]:
    'Pivots data against a list of index (row) columns, a key column, and a value column. All other columns are removed.',
  [LabAutomationTransformType.PLATE_SHAPE]:
    'Converts data in plate shape to a two column format (Well Coordinates and Values)',
  [LabAutomationTransformType.REMOVE_QUOTES]: 'Removes quotes that enclose entire cells.',
  [LabAutomationTransformType.SPLIT]: 'Splits a column by a delimiting character.',
};

export interface LabAutomationTransformFormProps {
  baseSchema: Schema;
  isFirstStep?: boolean;
  transform: LabAutomationTransform;
  handleSetTransform: (transform: LabAutomationTransform) => void;
  formErrors: FormErrors;
}

export default function LabAutomationTransformForm(
  props: LabAutomationTransformFormProps
): JSX.Element | null {
  const stepType = props.transform.transformType;
  const renderForm = stepType ? labAutomationTransformForm()[stepType]! : () => null;
  if (stepType && renderForm == null) {
    assert(false, `Expected to configure ${stepType} transform but form is not defined`);
    return null;
  }
  return (
    <>
      <Row className={css(styles.typeSelectorContainer)}>
        <Col md={4}>
          <FieldLabel label="Type" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <SelectDropdown
            className={css(styles.typeSelector)}
            alwaysDropDown={true}
            menu={
              <SearchableMenu
                height={Menu.HEIGHTS.MEDIUM}
                width={Menu.WIDTHS.FULL}
                selectedValue={props.transform?.transformType}
                onSelect={(type: LabAutomationTransformType) => {
                  props.handleSetTransform(getInitialTransformOfType(type));
                }}
                optionDatas={Object.values(LabAutomationTransformType)
                  .filter(
                    (transformType) =>
                      props.isFirstStep || transformType !== LabAutomationTransformType.CUSTOM
                  )
                  .map((option) => ({
                    name: capitalize(constantCaseToWords(option)),
                    value: option,
                  }))}
              />
            }
            displayName={
              props.transform.transformType
                ? capitalize(constantCaseToWords(props.transform.transformType))
                : 'Select type'
            }
            error={(props.formErrors as EmptyFormErrors).transformType ? 'Type is required' : null}
          />
        </Col>
        <Col md={8}>
          <span className={css(styles.description)}>
            {props.transform.transformType ? TRANSFORM_DESCRIPTIONS[props.transform.transformType] : null}
          </span>
        </Col>
      </Row>
      {props.transform.transformType
        ? renderForm({
            baseSchema: props.baseSchema,
            transform: props.transform,
            onChange: props.handleSetTransform,
            formErrors: props.formErrors,
          })
        : null}
    </>
  );
}

const styles = StyleSheet.create({
  description: {
    fontSize: 13,
    lineHeight: '16px',
    color: GRAY_DARKER,
  },
  typeSelector: {
    paddingBottom: '16px',
  },
  typeSelectorContainer: {
    display: 'flex',
    alignItems: 'center',
  },
});
